import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import { Play, Pause, SkipBack, SkipForward, Volume2, Loader2 } from 'lucide-react'
import { audioApi, type AudioSegments, type Reciter } from '@/services/audioApiService'

interface AudioPlayerProps {
  surahNumber: number
  ayahs: Array<{ number: number; text: string }>
  onWordHighlight?: (ayahNumber: number, wordIndex: number) => void
  onAyahChange?: (ayahNumber: number) => void
  initialReciterId?: string | null
}

type PlaybackMode = 'ayah' | 'surah'

export default function AudioPlayer({
  surahNumber,
  ayahs,
  onWordHighlight,
  onAyahChange,
  initialReciterId = null,
}: AudioPlayerProps) {
  const { t } = useTranslation()

  const [playbackMode, setPlaybackMode] = useState<PlaybackMode>('surah')
  const [reciters, setReciters] = useState<Reciter[]>([])
  const [selectedReciter, setSelectedReciter] = useState<Reciter | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentAyahIndex, setCurrentAyahIndex] = useState(0)
  const [volume, setVolume] = useState(70)
  const [audioSegments, setAudioSegments] = useState<AudioSegments | null>(null)
  const [error, setError] = useState<string | null>(null)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  // Fetch reciters on mount
  useEffect(() => {
    const fetchReciters = async () => {
      try {
        const data = await audioApi.getReciters()
        setReciters(data)

        if (data.length > 0) {
          // Use initialReciterId if provided, otherwise default to first reciter (usually ID 1 - AbdulBaset)
          let defaultReciter
          if (initialReciterId) {
            defaultReciter = data.find((r) => r.id === initialReciterId) || data.find((r) => r.id === '1') || data[0]
          } else {
            defaultReciter = data.find((r) => r.id === '1') || data[0]
          }
          setSelectedReciter(defaultReciter)
        }
      } catch (err) {
        console.error('Failed to fetch reciters:', err)
        // Set default reciter even if fetch fails
        setSelectedReciter({ id: '1', name: 'AbdulBaset AbdulSamad', nameArabic: 'عبد الباسط عبد الصمد' })
      }
    }

    fetchReciters()
  }, [initialReciterId])

  // Initialize audio element
  useEffect(() => {
    // Create new audio element
    const audio = new Audio()
    audio.preload = 'auto'
    // Remove crossOrigin to avoid CORS issues - let browser handle it
    // audio.crossOrigin = 'anonymous'

    audioRef.current = audio

    audio.addEventListener('ended', () => {
      handleAudioEnded()
    })
    audio.addEventListener('play', () => {
      setIsPlaying(true)
    })
    audio.addEventListener('pause', () => {
      setIsPlaying(false)
    })
    audio.addEventListener('error', handleAudioError)
    audio.addEventListener('loadstart', () => {
      setIsLoading(true)
    })
    audio.addEventListener('canplay', () => {
      setIsLoading(false)
    })

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      audio.pause()
      audio.removeEventListener('ended', handleAudioEnded)
      audio.removeEventListener('play', () => setIsPlaying(true))
      audio.removeEventListener('pause', () => setIsPlaying(false))
      audio.removeEventListener('error', handleAudioError)
      audio.removeEventListener('loadstart', () => setIsLoading(true))
      audio.removeEventListener('canplay', () => setIsLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update audio volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100
    }
  }, [volume])

  // Track playback time for word highlighting
  useEffect(() => {
    if (
      !isPlaying ||
      !audioRef.current ||
      !audioSegments?.segments ||
      audioSegments.segments.length === 0
    ) {
      return
    }

    const trackTime = () => {
      if (audioRef.current && audioSegments && audioSegments.segments) {
        const currentTime = audioRef.current.currentTime * 1000 // ms

        const currentSegment = audioSegments.segments.find(
          (seg) => currentTime >= seg.startTime && currentTime <= seg.endTime
        )

        if (currentSegment && onWordHighlight) {
          onWordHighlight(audioSegments.ayah, currentSegment.wordIndex)
        }
      }

      animationFrameRef.current = requestAnimationFrame(trackTime)
    }

    animationFrameRef.current = requestAnimationFrame(trackTime)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying, audioSegments, onWordHighlight])

  const handleAudioEnded = () => {
    // For full surah mode, just stop when audio ends
    setIsPlaying(false)
    setCurrentAyahIndex(0)
    if (audioRef.current) {
      audioRef.current.currentTime = 0
    }
  }

  const handleAudioError = (e: Event) => {
    console.error('Audio playback error:', e)
    setError(t('quran.audioError', 'فشل تشغيل الصوت'))
    setIsLoading(false)
    setIsPlaying(false)
  }

  const loadAndPlayAudio = async (audioUrl: string, segments: AudioSegments) => {
    if (!audioRef.current) return
    if (!audioUrl || audioUrl.trim() === '') {
      setError(t('quran.audioUrlMissing', 'رابط الصوت غير متوفر'))
      return
    }

    try {
      setError(null)
      setIsLoading(true)

      const audio = audioRef.current

      // Stop current playback and clear previous source
      audio.pause()
      audio.currentTime = 0
      
      // Remove all sources
      while (audio.firstChild) {
        audio.removeChild(audio.firstChild)
      }
      
      // Clear src
      audio.src = ''
      audio.removeAttribute('src')
      
      // Force load to clear previous state
      try {
        audio.load()
      } catch (e) {
        // Ignore load error during clearing
      }

      // Wait a bit before setting new source
      await new Promise(resolve => setTimeout(resolve, 200))

      // Set new source using source element for better compatibility
      const source = document.createElement('source')
      source.src = audioUrl
      source.type = 'audio/mpeg'
      audio.appendChild(source)
      
      // Also set src directly as fallback
      audio.src = audioUrl
      
      setAudioSegments(segments)
      
      // Wait until audio can play with timeout
      await new Promise<void>((resolve, reject) => {
        let resolved = false
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true
            audio.removeEventListener('canplay', handleCanPlay)
            audio.removeEventListener('canplaythrough', handleCanPlayThrough)
            audio.removeEventListener('loadeddata', handleLoadedData)
            audio.removeEventListener('error', handleError)
            reject(new Error('Audio load timeout'))
          }
        }, 10000) // 10 second timeout

        const handleCanPlay = () => {
          if (!resolved) {
            resolved = true
            clearTimeout(timeout)
            audio.removeEventListener('canplay', handleCanPlay)
            audio.removeEventListener('canplaythrough', handleCanPlayThrough)
            audio.removeEventListener('loadeddata', handleLoadedData)
            audio.removeEventListener('error', handleError)
            resolve()
          }
        }

        const handleCanPlayThrough = () => {
          if (!resolved) {
            resolved = true
            clearTimeout(timeout)
            audio.removeEventListener('canplay', handleCanPlay)
            audio.removeEventListener('canplaythrough', handleCanPlayThrough)
            audio.removeEventListener('loadeddata', handleLoadedData)
            audio.removeEventListener('error', handleError)
            resolve()
          }
        }

        const handleLoadedData = () => {
          if (!resolved) {
            resolved = true
            clearTimeout(timeout)
            audio.removeEventListener('canplay', handleCanPlay)
            audio.removeEventListener('canplaythrough', handleCanPlayThrough)
            audio.removeEventListener('loadeddata', handleLoadedData)
            audio.removeEventListener('error', handleError)
            resolve()
          }
        }

        const handleError = (ev: Event) => {
          if (!resolved) {
            resolved = true
            clearTimeout(timeout)
            audio.removeEventListener('canplay', handleCanPlay)
            audio.removeEventListener('canplaythrough', handleCanPlayThrough)
            audio.removeEventListener('loadeddata', handleLoadedData)
            audio.removeEventListener('error', handleError)
            
            const audioElement = ev.target as HTMLAudioElement
            const error = audioElement.error
            let errorMessage = t('quran.audioLoadFailed', 'فشل تحميل الصوت')
            
            if (error) {
              switch (error.code) {
                case error.MEDIA_ERR_ABORTED:
                  errorMessage = t('quran.audioAborted', 'تم إلغاء تحميل الصوت')
                  break
                case error.MEDIA_ERR_NETWORK:
                  errorMessage = t('quran.audioNetworkError', 'خطأ في الشبكة')
                  break
                case error.MEDIA_ERR_DECODE:
                  errorMessage = t('quran.audioDecodeError', 'خطأ في فك تشفير الصوت')
                  break
                case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                  errorMessage = t('quran.audioFormatUnsupported', 'تنسيق الصوت غير مدعوم')
                  break
                default:
                  errorMessage = t('quran.audioUnknownError', 'خطأ غير معروف في الصوت')
              }
            }
            
            reject(new Error(errorMessage))
          }
        }

        audio.addEventListener('canplay', handleCanPlay)
        audio.addEventListener('canplaythrough', handleCanPlayThrough)
        audio.addEventListener('loadeddata', handleLoadedData)
        audio.addEventListener('error', handleError)
        
        // Start loading
        audio.load()
      })

      // Wait a bit more to ensure audio is fully loaded
      if (audio.readyState < 3) {
        await new Promise(resolve => {
          const checkReady = () => {
            if (audio.readyState >= 3) {
              resolve(void 0)
            } else {
              setTimeout(checkReady, 100)
            }
          }
          checkReady()
        })
      }
      
      await audio.play()
      setIsPlaying(true)
      setIsLoading(false)
    } catch (err: any) {
      console.error('Failed to play audio:', err)
      setError(err.message || t('quran.audio.errorPlayingAudio', 'فشل تشغيل الصوت'))
      setIsLoading(false)
      setIsPlaying(false)
    }
  }

  const playAyah = async (ayahIndex: number) => {
    // For ayah mode, just play the full surah and seek to the ayah position
    // Since we're using full surah audio, we'll just play the surah
    await playSurah()
  }

  const playSurah = async () => {
    if (!ayahs || ayahs.length === 0) return
    if (!selectedReciter) {
      setError(t('quran.selectReciter', 'يرجى اختيار قارئ'))
      return
    }

    setCurrentAyahIndex(0)

    if (onAyahChange) {
      onAyahChange(ayahs[0].number)
    }

    try {
      setIsLoading(true)
      setError(null)

      const reciterId = typeof selectedReciter.id === 'string' 
        ? parseInt(selectedReciter.id, 10) 
        : selectedReciter.id;

      const segments = await audioApi.getSurahAudio(surahNumber, reciterId)
      
      if (!segments.audioUrl) {
        throw new Error('Audio URL is empty');
      }
      
      await loadAndPlayAudio(segments.audioUrl, segments)
    } catch (err: any) {
      console.error('Failed to load surah audio:', err)
      setError(err.message || t('quran.errorLoadAudio', 'فشل تحميل صوت السورة'))
      setIsLoading(false)
    }
  }

  const togglePlay = async () => {
    if (!audioRef.current) return

    try {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else {
        if (!audioRef.current.src || !audioRef.current.currentSrc) {
          // Always play full surah
          await playSurah()
        } else {
          try {
            await audioRef.current.play()
            setIsPlaying(true)
          } catch (playError: any) {
            console.error('Play error:', playError)
            // If play fails, reload the audio
            await playSurah()
          }
        }
      }
    } catch (err) {
      console.error('Toggle play error:', err)
      setError(t('quran.audioError', 'فشل تشغيل الصوت'))
    }
  }

  const playPrevious = () => {
    if (playbackMode === 'ayah' && currentAyahIndex > 0) {
      playAyah(currentAyahIndex - 1)
    }
  }

  const playNext = () => {
    if (
      playbackMode === 'ayah' &&
      ayahs &&
      currentAyahIndex < ayahs.length - 1
    ) {
      playAyah(currentAyahIndex + 1)
    }
  }

  // Always use surah mode
  useEffect(() => {
    setPlaybackMode('surah')
  }, [])

  const currentAyah =
    ayahs && ayahs.length > currentAyahIndex ? ayahs[currentAyahIndex] : null

  // Early return if no ayahs
  if (!ayahs || ayahs.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-center text-gray-500">{t('quran.noAyahs', 'لا توجد آيات للعرض')}</p>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Reciter Selection */}
      <Card className="p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('quran.chooseReciter', 'اختر القارئ:')}
        </label>
        <select
          value={selectedReciter?.id || ''}
          onChange={async (e) => {
            const reciter = reciters.find((r) => r.id === e.target.value)
            setSelectedReciter(reciter || null)

            // Stop current playback when changing reciter
            if (audioRef.current) {
              audioRef.current.pause()
              audioRef.current.src = ''
              audioRef.current.load() // Force reload
            }
            setAudioSegments(null)
            setIsPlaying(false)
            setError(null)
            
            // If audio was playing, reload with new reciter
            if (reciter && audioRef.current && audioRef.current.src) {
              try {
                setIsLoading(true)
                const reciterId = typeof reciter.id === 'string' 
                  ? parseInt(reciter.id, 10) 
                  : reciter.id;
                
                const segments = await audioApi.getSurahAudio(surahNumber, reciterId)
                if (segments.audioUrl) {
                  await loadAndPlayAudio(segments.audioUrl, segments)
                }
              } catch (err) {
                console.error('Failed to reload audio with new reciter:', err)
                setIsLoading(false)
              }
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={reciters.length === 0}
        >
          {reciters.length === 0 && <option>{t('common.loading')}</option>}
          {reciters.map((reciter) => (
            <option key={reciter.id} value={reciter.id}>
              {reciter.nameArabic || reciter.name}
            </option>
          ))}
        </select>
      </Card>

      {/* Playback Mode - Always full surah */}
      <Card className="p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('quran.playbackMode', 'نمط التشغيل:')}
        </label>
        <div className="px-4 py-2 rounded-lg bg-primary-600 text-white text-center">
          {t('quran.fullSurah', 'السورة كاملة')}
        </div>
      </Card>

      {/* Audio Controls */}
      <Card className="p-6">
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600 mb-1">
            {t('quran.surah', 'السورة')}
          </p>
          <p className="text-3xl font-bold text-primary-600">
            {surahNumber}
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 mb-6">
          <button
            onClick={() => {
              if (audioRef.current) {
                audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10)
              }
            }}
            disabled={isLoading || !audioRef.current?.src}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 transition-colors"
            aria-label={t('radio.rewind10', 'رجوع 10 ثواني')}
          >
            <SkipBack className="w-5 h-5" />
          </button>
          <button
            onClick={togglePlay}
            disabled={isLoading || !selectedReciter}
            className="p-4 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
            aria-label={isPlaying ? t('common.pause') : t('common.play')}
          >
            {isLoading ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-8 h-8" />
            ) : (
              <Play className="w-8 h-8" />
            )}
          </button>
          <button
            onClick={() => {
              if (audioRef.current) {
                audioRef.current.currentTime = Math.min(
                  audioRef.current.duration || 0,
                  audioRef.current.currentTime + 10
                )
              }
            }}
            disabled={isLoading || !audioRef.current?.src}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 transition-colors"
            aria-label={t('radio.forward10', 'تقديم 10 ثواني')}
          >
            <SkipForward className="w-5 h-5" />
          </button>
        </div>

        {/* Volume Control */}
        <div className="flex items-center gap-2">
          <Volume2 className="w-5 h-5 text-gray-500" />
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
            aria-label={t('radio.volume', 'الصوت')}
          />
          <span className="text-sm text-gray-600 w-12">{volume}%</span>
        </div>

        {/* Ayahs List (Ayah-by-Ayah mode only) */}
        {playbackMode === 'ayah' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">{t('quran.ayahs', 'الآيات:')}</p>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {ayahs.map((ayah, index) => (
                <button
                  key={ayah.number}
                  onClick={() => playAyah(index)}
                  disabled={isLoading}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    currentAyahIndex === index
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } disabled:opacity-50`}
                >
                  {ayah.number}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
